import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import {
  capitalize,
  editFormDataAF,
  getRideBookingsRouteMismatchSummary,
  IRideDto,
  onActivateKeyDown,
  rideBookingsAF,
  RideCrudModeKeyTuple,
  useDrawers,
  useFormats,
} from "@ryde/shared";

import { RideTabsBottomBookingsMismatchTooltipContent } from "./ride-tabs-bottom-bookings.tooltip";
import { getBookingStatusClassName } from "./ride-tabs-bottom.helpers";
import { RideBottomBlockStyled } from "./ride.styled";

export const RideTabsBottomBookings: FC = () => {
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const currentRideId = rideDto?.id || 0;
  const rideBookings = useRecoilValue(rideBookingsAF(currentRideId));
  const { openEditFormDrawer } = useDrawers();
  const { formatDateTimeNormalized } = useFormats();
  const { t } = useTranslation();

  const handleBookingClick = (bookingId: number): void => {
    openEditFormDrawer({
      entity: "booking",
      id: bookingId,
    });
  };

  const mismatchSummary = useMemo(
    () => getRideBookingsRouteMismatchSummary({ rideDto, rideBookings }),
    [rideDto, rideBookings],
  );

  const hasMismatch = useMemo(() => {
    const routeLen = rideDto?.metadata?.passengerIds?.length ?? 0;
    const countMismatch = routeLen !== (rideBookings?.length || 0);
    const gap =
      mismatchSummary.routeBookingIdsMissingFromRide.length > 0 ||
      mismatchSummary.routePassengerIdsWithoutRideBooking.length > 0 ||
      mismatchSummary.bookingIdsOnRideNotOnRoutePassengers.length > 0;
    return countMismatch || gap;
  }, [rideDto, rideBookings, mismatchSummary]);

  const bookingsLabel = capitalize(t("booking.plural"));

  const bookingIdsOffRoutePassengers = useMemo(
    () => new Set(mismatchSummary.bookingIdsOnRideNotOnRoutePassengers),
    [mismatchSummary.bookingIdsOnRideNotOnRoutePassengers],
  );

  if (!rideBookings?.length) {
    return null;
  }

  return (
    <RideBottomBlockStyled>
      <label>
        {hasMismatch ? (
          <FramerTooltip
            content={
              <RideTabsBottomBookingsMismatchTooltipContent
                mismatchSummary={mismatchSummary}
              />
            }
            rtlStrippedBottomLeftPlacement
          >
            <span className="ride-bookings-route-mismatch-label-trigger">
              <span
                className="ride-bookings-route-mismatch-warning-icon"
                aria-hidden
              >
                ⚠️
              </span>
              {`${bookingsLabel}:`}
            </span>
          </FramerTooltip>
        ) : (
          `${bookingsLabel}:`
        )}
      </label>
      <span>
        {rideBookings.map((booking, index) => {
          const tooltipContent = `${booking.id} - ${
            booking.bookTime
              ? formatDateTimeNormalized(booking.bookTime)
              : "N/A"
          } - ${booking.passengerCode} ${booking.passengerLastName}`;
          const isRoutePassengerMismatch = bookingIdsOffRoutePassengers.has(
            booking.id,
          );
          return (
            <span key={booking.id}>
              <FramerTooltip
                content={
                  hasMismatch && isRoutePassengerMismatch ? (
                    <RideTabsBottomBookingsMismatchTooltipContent
                      mismatchSummary={mismatchSummary}
                    />
                  ) : (
                    tooltipContent
                  )
                }
                rtlStrippedBottomLeftPlacement
              >
                <span
                  className={clsx(
                    "booking-id",
                    booking.status
                      ? getBookingStatusClassName(booking.status)
                      : "",
                    "pointer",
                    isRoutePassengerMismatch &&
                      "booking-id--route-passenger-mismatch",
                  )}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleBookingClick(booking.id)}
                  onKeyDown={onActivateKeyDown(() =>
                    handleBookingClick(booking.id),
                  )}
                >
                  {booking.id}
                </span>
              </FramerTooltip>
              {index < rideBookings.length - 1 && ", "}
            </span>
          );
        })}
      </span>
    </RideBottomBlockStyled>
  );
};
